import * as React from "react";
import { Select, IOption } from "../../atoms/forms/Select";
import { IApplicationState } from "../../../store";
import * as ActivityTypes from "../../../store/activitytypes";
import { FetchState } from "../../../entities/FetchState";
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { IData as ActivityTypeData } from '../../../store/activitytypes/activityTypesList';
import { IStoreSelectControls } from "./ICommonInputsControls";

// ActivityType interface from the store
type ActivityType = ActivityTypeData;


interface IActivityType extends IOption {
    activityType: ActivityType;
}

const mapActivityTypeToValue = (activityType: ActivityType): IActivityType => {
    return {
        value: activityType.id,
        label: activityType.description,
        activityType: activityType
    }
}

// Props interface
interface IProps {
    selectedActivityTypeId?: string;
}

// Actual Component
export type AllProps = IProps & IStoreSelectControls<ActivityType>;
export const ActivityTypeSelect = (props: AllProps) => {
    const { t: tDatatypes } = useTranslation('datatypes');
    const { t: tCommon } = useTranslation('common');
    
    // Get Redux state and dispatch
    const activityTypesState = useSelector((state: IApplicationState) => state.activitytypes.activityTypesList);
    const dispatch = useDispatch();

    // Internal state for selected activity type - initialize with preselected activity type if available
    const [selectedActivityType, setSelectedActivityType] = React.useState<IActivityType | null>(() => {
        // Try to initialize with preselected activity type if data is already available
        if (activityTypesState.ActivityTypesList.data && props.selectedActivityTypeId) {
            const activityType = activityTypesState.ActivityTypesList.data.find(a => a.id === props.selectedActivityTypeId);
            if (activityType) {
                return mapActivityTypeToValue(activityType);
            }
        }
        // If using value from common interface
        else if (props.value) {
            return mapActivityTypeToValue(props.value);
        }
        return null;
    });

    // Fetch activity types on initial render if not loaded or if the data array is empty
    React.useEffect(() => {
        const shouldFetch = 
            activityTypesState.ActivityTypesList.state === FetchState.NotFetched || 
            (activityTypesState.ActivityTypesList.state === FetchState.Success && 
             (!activityTypesState.ActivityTypesList.data || activityTypesState.ActivityTypesList.data.length === 0));
             
        if (shouldFetch) {
            dispatch(ActivityTypes.ActionCreators.activityTypesList.FetchData());
        }
    }, [dispatch, activityTypesState.ActivityTypesList.state, activityTypesState.ActivityTypesList.data]);

    // Update selected activity type when data is loaded or selectedActivityTypeId prop changes
    React.useEffect(() => {
        if (activityTypesState.ActivityTypesList.data) {
            if (props.selectedActivityTypeId) {
                const activityType = activityTypesState.ActivityTypesList.data.find(a => a.id === props.selectedActivityTypeId);
                if (activityType) {
                    const mappedActivityType = mapActivityTypeToValue(activityType);
                    setSelectedActivityType(mappedActivityType);
                } else {
                    setSelectedActivityType(null);
                }
            } else if (props.value) {
                // Handle value from common interface
                setSelectedActivityType(mapActivityTypeToValue(props.value));
            } else {
                setSelectedActivityType(null);
            }
        }
    }, [activityTypesState.ActivityTypesList.data, props.selectedActivityTypeId, props.value, activityTypesState.ActivityTypesList.state]);

    // Map activity types to select options
    const activityTypes = React.useMemo(() => {
        return (activityTypesState.ActivityTypesList.data || [])
            .map(mapActivityTypeToValue)
            .sort((a, b) => {
                if (a.label && b.label) {
                    return a.label.localeCompare(b.label);
                }
                // Fallback: compare by id if label is undefined
                return String(a.value).localeCompare(String(b.value));
            });
    }, [activityTypesState.ActivityTypesList.data, activityTypesState.ActivityTypesList.state]);

    // Force component re-render when activity types list changes state from loading to success
    const [forceUpdateKey, setForceUpdateKey] = React.useState(0);
    React.useEffect(() => {
        if (activityTypesState.ActivityTypesList.state === FetchState.Success && activityTypesState.ActivityTypesList.data && activityTypesState.ActivityTypesList.data.length > 0) {
            setForceUpdateKey(prev => prev + 1);
        }
    }, [activityTypesState.ActivityTypesList.state, activityTypesState.ActivityTypesList.data]);

    const updateActivityType = (activityType: IActivityType | null) => {
        setSelectedActivityType(activityType);
        
        // Call onChange callback from common interface
        if (props.onChange) {
            props.onChange(activityType?.activityType || null, !props.required || !!activityType);
        }
    }

    const isDisabled = props.disabled || activityTypesState.ActivityTypesList.state !== FetchState.Success;
    const isLoading = activityTypesState.ActivityTypesList.state === FetchState.Busy || activityTypesState.ActivityTypesList.state === FetchState.NotFetched || props.loading;
    const error = props.error || (activityTypesState.ActivityTypesList.state === FetchState.Error ? [true, tCommon('errorLoadingData')] : undefined);

    return (<Select 
        key={forceUpdateKey}
        label={(props.label || tDatatypes('activityTypeLabel') || "Activity Type") + (activityTypesState.ActivityTypesList.state !== FetchState.Success ? tCommon('loading') : "")}
        placeholder={props.placeholder || (tDatatypes('activityTypePlaceholder') || "Select an activity type...")}
        options={activityTypes}
        value={selectedActivityType}
        disabled={isDisabled}
        onChange={updateActivityType}
        loading={isLoading}
        error={error}
        required={props.required}
        prevalidate={props.prevalidate}
        icon={props.icon}
        isUnknownValuesEnabled={props.isUnknownValuesEnabled}
    />)
}

